<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

    <!-- Modal -->
    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
        <!-- Close button -->
        <div class="absolute right-0 top-0 pr-4 pt-4">
          <button 
            @click="closeModal" 
            class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Header -->
        <div class="sm:flex sm:items-start mb-6">
          <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
            <i class="fas fa-user-edit text-[#4d148c]"></i>
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 class="text-lg font-semibold leading-6 text-gray-900">Update Profile</h3>
            <p class="mt-1 text-sm text-gray-500">Update your personal information</p>
          </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit">
          <div class="space-y-4">
            <!-- Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700">Full Name</label>
              <input 
                v-model="formData.name"
                type="text"
                class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4d148c] focus:ring-[#4d148c] sm:text-sm"
                placeholder="Enter your full name"
              />
            </div>

            <!-- Phone -->
            <div>
              <label class="block text-sm font-medium text-gray-700">Phone Number</label>
              <input 
                v-model="formData.phone"
                type="text"
                class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4d148c] focus:ring-[#4d148c] sm:text-sm"
                placeholder="Enter your phone number"
              />
            </div>

            <!-- Address -->
            <div>
              <label class="block text-sm font-medium text-gray-700">Address</label>
              <textarea 
                v-model="formData.address"
                rows="3"
                class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4d148c] focus:ring-[#4d148c] sm:text-sm"
                placeholder="Enter your address"
              ></textarea>
            </div>

            <!-- Password -->
            <div>
              <label class="block text-sm font-medium text-gray-700">New Password (optional)</label>
              <input 
                v-model="formData.password"
                type="password"
                class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4d148c] focus:ring-[#4d148c] sm:text-sm"
                placeholder="Enter new password"
              />
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="mt-4 text-sm text-red-600">
            {{ error }}
          </div>

          <!-- Success Message -->
          <div v-if="success" class="mt-4 text-sm text-green-600">
            {{ success }}
          </div>

          <!-- Actions -->
          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              @click="closeModal"
              class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4d148c]"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="inline-flex justify-center rounded-md bg-[#4d148c] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#6d2ba8] focus:outline-none focus:ring-2 focus:ring-[#4d148c] disabled:opacity-50"
            >
              <i v-if="loading" class="fas fa-circle-notch fa-spin mr-2"></i>
              {{ loading ? 'Updating...' : 'Update Profile' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api';

export default {
  name: 'ProfileUpdateModal',
  props: {
    show: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      formData: {
        name: '',
        phone: '',
        address: '',
        password: ''
      },
      loading: false,
      error: '',
      success: ''
    }
  },
  async created() {
    await this.loadUserData();
  },
  methods: {
    async loadUserData() {
      try {
        const response = await api.get('/api/get-user-data');
        if (response.data.success) {
          const userData = response.data.data;
          this.formData.name = userData.name || '';
          this.formData.phone = userData.phone || '';
          this.formData.address = userData.address || '';
        }
      } catch (error) {
        this.error = 'Failed to load user data';
        console.error('Error loading user data:', error);
      }
    },
    async handleSubmit() {
      this.loading = true;
      this.error = '';
      this.success = '';

      try {
        const response = await api.post('/api/update-user', this.formData);
        if (response.data.success) {
          this.success = 'Profile updated successfully';
          this.$emit('profile-updated', this.formData);
          
          // Reset password field
          this.formData.password = '';
          
          // Close modal after 1.5 seconds
          setTimeout(() => {
            this.closeModal();
          }, 1500);
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to update profile';
        console.error('Error updating profile:', error);
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.$emit('close');
      this.error = '';
      this.success = '';
    }
  }
}
</script>